function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import ServiceFactory from "../../service";
import Auth from "../../functions/authenticate";
import { num, fetchSales, titleCase } from "../sales/territory-common";
import { getContactTimeline, getContactCustomers } from "../people/api";
import { getGlobalRange } from "../../functions/period-store";
import { getConsent, erasePerson, isAdministrator, toast } from "../audiences/api";
import EnrichModal from "./enrich-modal";
import EditContactModal from "./edit-contact-modal";
import { ListPicker, StageChip, stageKey, asDate, monthDay, shortWhen } from "./index-v2";

/*
 * A contact (People redesign 2026-10-09, PlunkDocs mockups/people-redesign-2026-10-09.html board 2).
 *
 * Data:
 *   /v1/people/contacts/{id}/timeline     stats, nudge and the History list (new; null until deployed)
 *   /contacts/{id}                        identity, company, role, emails, phones, address, lists
 *   /contacts/{id}/summary/all_time       stats fallback
 *   /contacts/{id}/sessions/all_time      History fallback (one row per visit) + expand per session
 *   /contacts/{id}/sessions/detail/{sid}  pages and events inside one visit
 *   /contacts/{id}/products/all_time      "Products viewed" grid
 *   /contacts/{id}/locations · /pages     "Where and what"
 *   /lifecycle/customers/...              stage and the customer this contact belongs to
 *   /v1/person/{id}/consent, /erase       email permission and admin-only erase
 * The legacy /contacts/{id}/{range} history blob is deliberately not fetched (504s on big histories).
 * Person ids exceed 2^53: String() only.
 */

// "Screen", "Mobile", "Tablet" for the device tag on a visit.
const titleDevice = d => {
  const v = String(d || "").trim().toLowerCase();
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : "";
};

// Tracker times are stored in UTC without a zone marker; read them as UTC so they show in the viewer's local time
// (the same clock the History rows use).
const asUtc = v => typeof v === "string" && !/(Z|[+-]\d\d:?\d\d)$/i.test(v.trim()) ? asDate(v.trim().replace(" ", "T") + "Z") : asDate(v);

// "10:48 AM" for a dated history row; empty when the time is midnight (a date-only value).
const clockOf = at => {
  if (!at) return "";
  const d = new Date(at);
  if (isNaN(d) || d.getHours() === 0 && d.getMinutes() === 0) return "";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit"
  });
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SESSIONS_PAGE = 10;
const TIMELINE_PAGE = 50;
const TILE_TINTS = ["#e9e4dc", "#dfe6ea", "#ece3e6", "#e4e9e1", "#e6e3ee", "#efe8dc"];
const TYPE_ICON = {
  visit: "◉",
  email: "✉",
  form: "✎",
  engagement: "↗",
  order: "$",
  joined: "+"
};
const FILTERS = [["all", "Everything"], ["visit", "Visits"], ["form", "Conversions"], ["engagement", "Engagements"], ["email", "Emails"], ["order", "Orders"]];
const HARD_BOUNCE = ["hard_bounce", "invalid_syntax", "provider_cleaned", "erased"];
const ASKED_OUT = ["unsubscribe", "complaint", "manual", "global_block"];
function svc(api) {
  const Service = ServiceFactory.getType(api); // 'activity' root
  return new Service();
}
async function safe(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}
// lifecycle and consent payloads may arrive PascalCase
function g(o, key) {
  if (!o) return undefined;
  if (o[key] !== undefined) return o[key];
  return o[key.charAt(0).toUpperCase() + key.slice(1)];
}
function clock(d) {
  const hr = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${hr % 12 || 12}:${m} ${hr < 12 ? "AM" : "PM"}`;
}
/** History time: "Today 10:42", "Yesterday", "Oct 5". */
function histWhen(v) {
  const d = asDate(v);
  if (!d) return "";
  const s = shortWhen(d);
  if (s === "Today") return `Today ${clock(d)}`;
  if (s === "Yesterday") return s;
  return monthDay(d);
}
/** Full readable date: "Oct 5, 2026". */
function fullDate(v) {
  const d = asDate(v);
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : "";
}
function lowerWhen(v) {
  const s = shortWhen(v);
  return s === "Today" || s === "Yesterday" ? s.toLowerCase() : monthDay(v);
}
function plural(n, one, many) {
  return `${num(n)} ${Number(n) === 1 ? one : many}`;
}

// Tracker page titles arrive as " | Brand"-style junk; prefer a readable label from the URL path.
function pageLabel(name, url) {
  const clean = String(name || "").replace(/^[\s|]+/, "").replace(/[\s|]+$/, "").trim();
  let junk = !clean || clean.length <= 2;
  if (!junk) {
    try {
      const host = new URL(url).hostname.toLowerCase().replace(/[^a-z]/g, "");
      const squished = clean.toLowerCase().replace(/[^a-z]/g, "");
      if (squished && host.indexOf(squished) > -1) junk = true;
    } catch (e) {/* keep the title */}
  }
  if (!junk) return clean;
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    if (path === "/" || path === "") return "Home";
    return path.split("/").filter(Boolean).slice(-2).join(" / ").replace(/[-_]/g, " ");
  } catch (e) {
    return clean || "a page";
  }
}
function sourceWords(src) {
  const s = String(src || "").toLowerCase();
  if (!s) return "";
  if (/form|web|site|signup|sign_up|popup/.test(s)) return "your website form";
  if (/import|csv|upload/.test(s)) return "an import";
  if (/shopify|checkout|order/.test(s)) return "checkout";
  if (/api/.test(s)) return "a connected app";
  return s.replace(/_/g, " ");
}
function tintFor(key) {
  let h = 0;
  String(key || "").split("").forEach(c => {
    h = (h * 31 + c.charCodeAt(0)) % 997;
  });
  return TILE_TINTS[h % TILE_TINTS.length];
}
class ContactPageComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onDocDown", e => {
      if (this.state.moreMenu && this._moreEl && !this._moreEl.contains(e.target)) this.setState({
        moreMenu: false
      });
    });
    // ------------------------------------------------------------- history
    _defineProperty(this, "setFilter", async filter => {
      if (filter === this.state.filter) return;
      this.setState({
        filter
      });
      if (!this.state.timeline) return; // fallback history is visits only, filtered client-side
      this.setState({
        tlLoading: true
      });
      const r = await getContactTimeline(this.id(), {
        top: TIMELINE_PAGE,
        type: filter === "all" ? undefined : filter
      }).catch(() => null);
      if (!this._alive || this.state.filter !== filter) return;
      const items = r && r.items || [];
      this.setState({
        tlItems: items,
        tlMore: items.length >= TIMELINE_PAGE,
        tlLoading: false
      });
    });
    _defineProperty(this, "moreTimeline", async () => {
      const {
        filter,
        tlItems
      } = this.state;
      this.setState({
        tlLoading: true
      });
      const r = await getContactTimeline(this.id(), {
        top: TIMELINE_PAGE,
        skip: tlItems.length,
        type: filter === "all" ? undefined : filter
      }).catch(() => null);
      if (!this._alive) return;
      const items = r && r.items || [];
      this.setState({
        tlItems: [...tlItems, ...items],
        tlMore: items.length >= TIMELINE_PAGE,
        tlLoading: false
      });
    });
    _defineProperty(this, "moreSessions", async () => {
      if (this.state.loadingMore) return;
      this.setState({
        loadingMore: true
      });
      const page = await safe(svc(`/contacts/${this.id()}/sessions/all_time?$top=${SESSIONS_PAGE}&$skip=${this.state.sessions.length}`).getAsync(), {
        items: []
      });
      if (!this._alive) return;
      this.setState({
        sessions: [...this.state.sessions, ...(page && page.items || [])],
        loadingMore: false
      });
    });
    _defineProperty(this, "toggleSession", sid => {
      const open = {
        ...this.state.open,
        [sid]: !this.state.open[sid]
      };
      this.setState({
        open
      });
      if (open[sid] && !this.state.events[sid]) {
        this.setState({
          events: {
            ...this.state.events,
            [sid]: "loading"
          }
        });
        svc(`/contacts/${this.id()}/sessions/detail/${sid}`).getAsync().then(rows => {
          if (this._alive) this.setState({
            events: {
              ...this.state.events,
              [sid]: rows || []
            }
          });
        }).catch(() => {
          if (this._alive) this.setState({
            events: {
              ...this.state.events,
              [sid]: "error"
            }
          });
        });
      }
    });
    _defineProperty(this, "exportContact", () => {
      const {
        data
      } = this.state;
      this.setState({
        moreMenu: false
      });
      if (!data) return;
      const {
        contact,
        summary
      } = data;
      const emails = (contact.email || []).map(e => e.emailAddress).filter(Boolean).join("; ");
      const phones = (contact.phone || []).map(p => p.phoneNumber).filter(Boolean).join("; ");
      const company = ((contact.experience || [])[0] || {}).company;
      const q = v => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
      const cols = [["first_name", contact.firstName], ["last_name", contact.lastName], ["emails", emails], ["phones", phones], ["company", company && company.name], ["visits", summary && summary.sessions], ["page_views", summary && summary.pageViews], ["forms", summary && summary.conversions], ["first_seen", summary && summary.firstSeen], ["last_seen", summary && summary.lastSeen]];
      const csv = cols.map(([k]) => k).join(",") + "\n" + cols.map(([, v]) => q(v)).join(",") + "\n";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([csv], {
        type: "text/csv"
      }));
      a.download = `contact-${this.id()}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    });
    _defineProperty(this, "erase", async () => {
      this.setState({
        erasing: true,
        eraseErr: ""
      });
      try {
        await erasePerson(this.id());
        if (!this._alive) return;
        toast("Contact erased. Only a scrambled copy of the address is kept so a re-import can't bring them back.");
        this.setState({
          eraseConfirm: false,
          erasing: false,
          data: null
        });
        this.load();
      } catch (e) {
        if (this._alive) this.setState({
          erasing: false,
          eraseErr: e.status === 403 || e.status === 401 ? "Only an administrator can erase a contact." : e.message || "Erase didn't work. Try again."
        });
      }
    });
    this.state = {
      data: null,
      error: false,
      errorMessage: "",
      filter: "all",
      timeline: null,
      // /v1/people timeline payload, or null when not deployed
      tlItems: [],
      tlMore: false,
      tlLoading: false,
      sessions: [],
      sessionCount: 0,
      loadingMore: false,
      open: {},
      events: {},
      // sessionId -> open / rows | "loading" | "error"
      consent: undefined,
      enriching: false,
      editing: false,
      picker: false,
      moreMenu: false,
      eraseConfirm: false,
      erasing: false,
      eraseErr: ""
    };
    this._alive = true;
  }
  id() {
    return String(this.props.params.id);
  }
  async componentDidMount() {
    document.addEventListener("mousedown", this.onDocDown);
    if (Auth.isAuthenticated() || (await Auth.attachOrSignIn())) this.load();
  }
  componentWillUnmount() {
    this._alive = false;
    document.removeEventListener("mousedown", this.onDocDown);
  }
  componentDidUpdate(prev) {
    if (String(prev.params.id) !== this.id()) {
      this.setState({
        data: null,
        error: false,
        filter: "all",
        timeline: null,
        tlItems: [],
        sessions: [],
        sessionCount: 0,
        open: {},
        events: {},
        consent: undefined
      });
      this.load();
    }
  }
  async load() {
    const id = this.id();
    this.loadConsent();
    try {
      let tl = null;
      const [contact, summary, products, lifecycle, timeline, locations, pages] = await Promise.all([svc("/contacts").getAsync({
        id
      }), safe(svc(`/contacts/${id}/summary/all_time`).getAsync(), null), safe(svc(`/contacts/${id}/products/all_time`).getAsync(), []), this.resolveLifecycle(id), getContactTimeline(id, {
        top: TIMELINE_PAGE
      }).catch(() => null), safe(svc(`/contacts/${id}/locations?$top=5`).getAsync(), null), safe(svc(`/contacts/${id}/pages?$top=5`).getAsync(), null)]);
      if (!this._alive) return;
      if (!contact) {
        this.setState({
          error: true,
          errorMessage: "We couldn't find this contact."
        });
        return;
      }
      tl = timeline && Array.isArray(timeline.items) ? timeline : null;
      let sessions = {
        items: [],
        count: 0
      };
      if (!tl) sessions = await safe(svc(`/contacts/${id}/sessions/all_time?$top=${SESSIONS_PAGE}`).getAsync(), {
        items: [],
        count: 0
      });
      if (!this._alive) return;
      this.setState({
        data: {
          contact,
          summary,
          products: Array.isArray(products) ? products : products && products.items || [],
          lifecycle,
          locations,
          pages
        },
        timeline: tl,
        tlItems: tl ? tl.items : [],
        tlMore: tl ? tl.items.length >= TIMELINE_PAGE : false,
        sessions: sessions.items || [],
        sessionCount: Number(sessions.count) || 0
      });
    } catch (err) {
      if (this._alive) this.setState({
        error: true,
        errorMessage: err.message
      });
    }
  }
  async loadConsent() {
    try {
      const c = await getConsent(this.id());
      if (this._alive) this.setState({
        consent: c
      });
    } catch (e) {
      if (this._alive) this.setState({
        consent: null
      });
    }
  }

  // Contact to customer linkage: the accounts the contact is linked to (customer_person_map or the
  // customer's own person), else exact email match through the customers search (q=), else a unique
  // exact full-name match. `accounts` lists every linked account (the first one drives the lifecycle).
  async resolveLifecycle(id) {
    const linked = await getContactCustomers(id);
    const accounts = linked && linked.items || [];
    if (accounts.length) {
      const first = accounts[0];
      const lc = await safe(fetchSales(`/lifecycle/customers/${encodeURIComponent(String(first.id))}`), null);
      if (lc && lc.customer) return {
        ...lc,
        accounts
      };
      return {
        customer: {
          id: first.id,
          name: first.name,
          customerNumber: first.customerNumber
        },
        accounts
      };
    }
    try {
      const contact = await svc("/contacts").getAsync({
        id
      });
      if (!contact) return null;
      const emails = (contact.email || []).map(e => String(e.emailAddress || "").toLowerCase()).filter(Boolean).slice(0, 3);
      for (const em of emails) {
        const res = await safe(fetchSales(`/lifecycle/customers?q=${encodeURIComponent(em)}&top=3`), null);
        const hit = res && (res.rows || []).find(r => String(g(r, "email") || "").toLowerCase() === em);
        if (hit) return await safe(fetchSales(`/lifecycle/customers/${g(hit, "id")}`), null);
      }
      const name = String(contact.fullName || "").trim().toLowerCase();
      if (name) {
        const res = await safe(fetchSales(`/lifecycle/customers?q=${encodeURIComponent(name)}&top=2`), null);
        if (res && res.totalCount === 1 && res.rows && res.rows.length === 1) {
          const r = res.rows[0];
          if (String(g(r, "name") || "").trim().toLowerCase() === name) {
            return await safe(fetchSales(`/lifecycle/customers/${g(r, "id")}`), null);
          }
        }
      }
    } catch (e) {/* best effort */}
    return null;
  }
  historyRows() {
    const {
      timeline,
      tlItems,
      sessions
    } = this.state;
    if (timeline) {
      return tlItems.map((it, i) => ({
        key: `t${i}`,
        type: String(it.type || "").toLowerCase(),
        title: it.title,
        detail: it.detail,
        at: it.at,
        sessionId: it.sessionId != null ? String(it.sessionId) : null,
        conversions: Number(it.conversions) || 0,
        engagements: Number(it.engagements) || 0,
        device: it.device || null
      }));
    }
    // Fallback: one row per visit from the sessions endpoint.
    return sessions.map(s => {
      const parts = [];
      if (Number(s.productViews) > 0) parts.push(`looked at ${plural(s.productViews, "product", "products")}`);
      if (Number(s.conversions) > 0) parts.push(Number(s.conversions) === 1 ? "filled in a form" : `filled in ${num(s.conversions)} forms`);
      return {
        key: `s${s.sessionId}`,
        type: "visit",
        title: `Visited ${plural(s.pageViews, "page", "pages")}${parts.length ? `, ${parts.join(" and ")}` : ""}`,
        detail: s.deviceType ? `On a ${String(s.deviceType).toLowerCase()}` : "",
        at: asUtc(s.startDate),
        sessionId: s.sessionId != null ? String(s.sessionId) : null,
        conversions: Number(s.conversions) || 0,
        engagements: Number(s.engagements) || 0,
        device: s.deviceType || null
      };
    });
  }
  renderEvent(ev, i) {
    const kind = String(ev.kind || "").toLowerCase();
    const label = pageLabel(ev.name, ev.url);
    const d = asUtc(ev.date);
    let what;
    if (kind === "conversion") what = /*#__PURE__*/React.createElement("span", null, "Filled in ", /*#__PURE__*/React.createElement("b", null, ev.type || label));else if (kind === "engagement") what = /*#__PURE__*/React.createElement("span", null, ev.type || "Clicked", " on ", /*#__PURE__*/React.createElement("b", null, label));else what = /*#__PURE__*/React.createElement("span", null, "Viewed ", /*#__PURE__*/React.createElement("b", null, label));
    return /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "pc-evt" + (kind && kind !== "view" ? " hi" : "")
    }, /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, d ? clock(d) : ""), what);
  }
  renderHistory() {
    const {
      filter,
      timeline,
      tlMore,
      tlLoading,
      sessions,
      sessionCount,
      loadingMore,
      open,
      events
    } = this.state;
    const rows = this.historyRows().filter(r => timeline || filter === "all" || r.type === filter);
    const emptyCopy = {
      all: "Nothing yet. Visits, emails and orders show up here as they happen.",
      visit: "No visits to your site yet.",
      email: "No emails sent to them yet.",
      order: "No orders yet."
    }[filter];
    return /*#__PURE__*/React.createElement("section", {
      className: "pc-card pc-history"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pc-card-head"
    }, /*#__PURE__*/React.createElement("b", {
      className: "pc-grow"
    }, "History"), FILTERS.map(([k, label]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      type: "button",
      className: "pc-pill" + (filter === k ? " on" : ""),
      onClick: () => this.setFilter(k)
    }, label))), tlLoading && !rows.length ? /*#__PURE__*/React.createElement("p", {
      className: "pc-muted pc-pad"
    }, "Loading\u2026") : null, !tlLoading && !rows.length ? /*#__PURE__*/React.createElement("p", {
      className: "pc-muted pc-pad"
    }, emptyCopy) : null, rows.map(r => {
      const canOpen = r.type === "visit" && !!r.sessionId;
      const isOpen = canOpen && !!open[r.sessionId];
      const evs = canOpen ? events[r.sessionId] : null;
      return /*#__PURE__*/React.createElement("div", {
        key: r.key,
        className: "pc-hrow" + (isOpen ? " open" : "")
      }, /*#__PURE__*/React.createElement("div", {
        className: "pc-hrow-main" + (canOpen ? " click" : ""),
        role: canOpen ? "button" : undefined,
        tabIndex: canOpen ? 0 : undefined,
        onClick: canOpen ? () => this.toggleSession(r.sessionId) : undefined,
        onKeyDown: canOpen ? e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.toggleSession(r.sessionId);
          }
        } : undefined
      }, /*#__PURE__*/React.createElement("span", {
        className: `pc-ico ${r.type}`,
        "aria-hidden": "true"
      }, TYPE_ICON[r.type] || "•"), /*#__PURE__*/React.createElement("span", {
        className: "pc-hrow-text"
      }, /*#__PURE__*/React.createElement("span", {
        className: "pc-hrow-title"
      }, r.title, r.conversions > 0 ? /*#__PURE__*/React.createElement("span", {
        className: "pc-flag conv"
      }, r.conversions === 1 ? "Conversion" : `${num(r.conversions)} conversions`) : null, r.engagements > 0 ? /*#__PURE__*/React.createElement("span", {
        className: "pc-flag eng"
      }, r.engagements === 1 ? "1 engagement" : `${num(r.engagements)} engagements`) : null, r.device ? /*#__PURE__*/React.createElement("span", {
        className: "pc-flag dev"
      }, titleDevice(r.device)) : null), r.detail ? /*#__PURE__*/React.createElement("span", {
        className: "pc-muted"
      }, r.detail) : null), /*#__PURE__*/React.createElement("span", {
        className: "pc-muted pc-when"
      }, histWhen(r.at), clockOf(r.at) ? /*#__PURE__*/React.createElement("span", {
        className: "pc-clock"
      }, clockOf(r.at)) : null), /*#__PURE__*/React.createElement("span", {
        className: "material-icons pc-chev" + (canOpen ? "" : " blank"),
        "aria-hidden": "true"
      }, canOpen ? isOpen ? "expand_less" : "expand_more" : "expand_more")), isOpen ? /*#__PURE__*/React.createElement("div", {
        className: "pc-hrow-body"
      }, evs === "loading" ? /*#__PURE__*/React.createElement("p", {
        className: "pc-muted"
      }, "Loading\u2026") : null, evs === "error" ? /*#__PURE__*/React.createElement("p", {
        className: "pc-muted"
      }, "We couldn't load this visit.") : null, Array.isArray(evs) ? evs.map((ev, i) => this.renderEvent(ev, i)) : null, Array.isArray(evs) && !evs.length ? /*#__PURE__*/React.createElement("p", {
        className: "pc-muted"
      }, "Nothing recorded on this visit.") : null) : null);
    }), timeline && tlMore ? /*#__PURE__*/React.createElement("div", {
      className: "pc-more"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: this.moreTimeline,
      disabled: tlLoading
    }, tlLoading ? "Loading…" : "Show more")) : null, !timeline && sessions.length < sessionCount && (filter === "all" || filter === "visit") ? /*#__PURE__*/React.createElement("div", {
      className: "pc-more"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: this.moreSessions,
      disabled: loadingMore
    }, loadingMore ? "Loading…" : "Show more")) : null);
  }

  // ----------------------------------------------------------- right rail

  renderStats(contact, summary, lifecycle) {
    const st = this.state.timeline && this.state.timeline.stats || {};
    const sm = summary || {};
    const cust = lifecycle && lifecycle.customer;
    const pick = (a, b) => a != null ? a : b;
    const orders = pick(st.orders, pick(g(cust, "totalOrders"), g(cust, "orderCount")));
    const tiles = [[num(pick(st.visits, sm.sessions)), "Visits"], [num(sm.pageViews), "Page views"], [num(sm.productViews), "Products viewed"], [num(pick(st.conversions, sm.conversions)), "Conversions"], [num(pick(st.engagements, sm.engagements)), "Engagements"], [Number(st.emailsSent) > 0 ? `${num(st.emailsOpened)} of ${num(st.emailsSent)}` : "None sent", "Emails opened"], [num(orders), "Orders"], [fullDate(pick(st.firstSeen, sm.firstSeen)) || "Not yet", "First seen"], [fullDate(pick(st.lastSeen, sm.lastSeen)) || "Not yet", "Last seen"]];
    return /*#__PURE__*/React.createElement("section", {
      className: "pc-card pc-stats"
    }, tiles.map(([v, l]) => /*#__PURE__*/React.createElement("span", {
      key: l,
      className: "pc-stat"
    }, /*#__PURE__*/React.createElement("b", null, v), /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, l))));
  }
  renderAbout(contact, lifecycle) {
    const cust = lifecycle && lifecycle.customer;
    const experience = contact.experience || [];
    const exp = experience[0] || {};
    const company = exp.company && exp.company.name || g(cust, "name") || "";
    const custId = g(cust, "id");
    const emails = (contact.email || []).map(e => e.emailAddress).filter(Boolean);
    const phones = (contact.phone || []).map(p => p.phoneNumber).filter(Boolean);
    const address = (contact.address || []).map(a => String(a.name || "").replace(/\+/g, ", ")).filter(Boolean)[0] || [g(cust, "shipToCity"), g(cust, "shipToRegion")].filter(Boolean).join(", ");
    const lists = (contact.audience || []).map(a => typeof a === "string" ? {
      name: a
    } : {
      name: a && (a.name || a.audienceName),
      id: a && (a.id != null ? a.id : a.audienceId)
    }).filter(a => a.name);
    const demo = contact.demographic || {};
    const household = demo.household || {};
    const details = [];
    const accounts = lifecycle && lifecycle.accounts || [];
    if (accounts.length > 1) {
      details.push(["Accounts", accounts.map((a, i) => /*#__PURE__*/React.createElement("span", {
        key: a.id
      }, i ? ", " : "", /*#__PURE__*/React.createElement(Link, {
        to: `/Customers/account/${encodeURIComponent(String(a.id))}`
      }, titleCase(a.name || a.customerNumber || "Account"))))]);
    } else if (company) details.push(["Company", custId ? /*#__PURE__*/React.createElement(Link, {
      to: `/Customers/account/${encodeURIComponent(String(custId))}`
    }, titleCase(company)) : titleCase(company)]);
    if (exp.title) details.push(["Role", exp.title]);
    emails.slice(0, 3).forEach((v, i) => details.push([i === 0 ? emails.length > 1 ? "Emails" : "Email" : "", v]));
    phones.slice(0, 2).forEach((v, i) => details.push([i === 0 ? phones.length > 1 ? "Phones" : "Phone" : "", v]));
    if (address) details.push(["Location", address]);
    if (contact.birthDate) {
      const age = Math.floor((Date.now() - new Date(contact.birthDate).getTime()) / 31557600000);
      if (age > 0 && age < 120) details.push(["Age", String(age)]);
    }
    if (demo.gender) details.push(["Gender", demo.gender]);
    if (household.householdIncome) details.push(["Household income", household.householdIncome]);
    if (g(cust, "assignedRepId")) details.push(["Rep", g(cust, "assignedRepName") || g(cust, "assignedRepId")]);

    // Stage history: current stage first, then earlier moves (newest first), one line per stage.
    const stageLines = [];
    if (g(cust, "lifecycleStage")) stageLines.push({
      date: g(cust, "stageEnteredDate"),
      stage: g(cust, "lifecycleStage"),
      via: null
    });
    [...(lifecycle && lifecycle.history || [])].reverse().forEach(h => {
      const s = g(h, "toStage");
      if (s && !stageLines.some(l => String(l.stage).toLowerCase() === String(s).toLowerCase())) {
        stageLines.push({
          date: g(h, "changedDate"),
          stage: s,
          via: String(g(h, "eventType") || g(h, "source") || "").replace(/_/g, " ")
        });
      }
    });
    const interests = (contact.interest || []).filter(Boolean);
    const seen = {};
    const social = (contact.profile || []).filter(x => x && x.url && x.network).filter(x => seen[x.network + x.url] ? false : seen[x.network + x.url] = true).map(x => ({
      ...x,
      href: x.url.indexOf("http") === 0 ? x.url : `https://${x.url}`
    }));
    const profLines = experience.map(x => [x.title, x.company && x.company.name || ""].filter(Boolean).join(" · ")).filter(Boolean);
    const thin = details.length <= 2 && !interests.length && !social.length && !profLines.length;
    const sec = (label, node) => node ? /*#__PURE__*/React.createElement("div", {
      className: "pc-sec"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pc-sec-h"
    }, label), node) : null;
    return /*#__PURE__*/React.createElement("section", {
      className: "pc-card pc-about"
    }, /*#__PURE__*/React.createElement("b", {
      className: "pc-card-title"
    }, "About"), thin ? /*#__PURE__*/React.createElement("p", {
      className: "pc-muted"
    }, "Not much here yet. Use ", /*#__PURE__*/React.createElement("a", {
      className: "pc-link",
      onClick: () => this.setState({
        enriching: true
      })
    }, "Fill in details"), " to look them up.") : null, sec("Details", details.length ? details.map(([k, v], i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "pc-kv"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, k), /*#__PURE__*/React.createElement("span", null, v))) : null), sec("Lists", lists.length ? /*#__PURE__*/React.createElement("div", {
      className: "pc-chips"
    }, lists.map((a, i) => a.id != null ? /*#__PURE__*/React.createElement(Link, {
      key: i,
      className: "pc-chip",
      to: `/Audiences/Detail/${encodeURIComponent(String(a.id))}/${getGlobalRange("this_month")}`
    }, a.name) : /*#__PURE__*/React.createElement("span", {
      key: i,
      className: "pc-chip"
    }, a.name))) : null), sec("Stage history", stageLines.length ? stageLines.map((l, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "pc-kv"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, fullDate(l.date) || "Date unknown"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(StageChip, {
      stage: l.stage
    }), !stageKey(l.stage) ? String(l.stage) : null, l.via ? /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, " from ", l.via) : null))) : null), sec("Interests", interests.length ? /*#__PURE__*/React.createElement("div", {
      className: "pc-chips"
    }, interests.slice(0, 12).map((x, i) => /*#__PURE__*/React.createElement("span", {
      key: i,
      className: "pc-chip"
    }, x))) : null), sec("Social", social.length ? social.map((x, i) => /*#__PURE__*/React.createElement("a", {
      key: i,
      className: "pc-line",
      href: x.href,
      target: "_blank",
      rel: "noreferrer"
    }, x.href.replace(/^https?:\/\/(www\.)?/, ""))) : null), sec("Professional", profLines.length ? profLines.map((x, i) => /*#__PURE__*/React.createElement("span", {
      key: i,
      className: "pc-line"
    }, x)) : null));
  }
  renderWhereWhat(locations, pages) {
    const locRows = (locations && locations.items || (Array.isArray(locations) ? locations : [])).filter(r => String(r.city || r.location || "").trim());
    const pageRows = pages && pages.items || (Array.isArray(pages) ? pages : []);
    if (!locRows.length && !pageRows.length) return null;
    const topPages = Object.values(pageRows.reduce((acc, r) => {
      // the same page reached by two URLs (utm) is one row
      const k = pageLabel(r.title || r.name, r.url || r.page);
      const v = Number(r.views != null ? r.views : r.impressions) || 0;
      // the link drops tracking query strings (utm etc.) so it opens the clean page
      const href = (() => {
        const u = String(r.url || r.page || "");
        if (!/^https?:\/\//i.test(u)) return null;
        try {
          const x = new URL(u);
          x.search = "";
          x.hash = "";
          return x.toString();
        } catch (e) {
          return null;
        }
      })();
      if (acc[k]) {
        acc[k].views += v;
        if (!acc[k].href) acc[k].href = href;
      } else acc[k] = {
        label: k,
        views: v,
        href
      };
      return acc;
    }, {})).sort((a, b) => b.views - a.views).slice(0, 5);
    return /*#__PURE__*/React.createElement("section", {
      className: "pc-card"
    }, /*#__PURE__*/React.createElement("b", {
      className: "pc-card-title"
    }, "Where and what"), /*#__PURE__*/React.createElement("div", {
      className: "pc-twin pc-stack"
    }, locRows.length ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "pc-sec-h"
    }, "Locations"), /*#__PURE__*/React.createElement("table", {
      className: "pc-mini"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "City"), /*#__PURE__*/React.createElement("th", {
      className: "r"
    }, "Views"), /*#__PURE__*/React.createElement("th", {
      className: "r"
    }, "Sessions"))), /*#__PURE__*/React.createElement("tbody", null, locRows.slice(0, 5).map((r, i) => /*#__PURE__*/React.createElement("tr", {
      key: i
    }, /*#__PURE__*/React.createElement("td", null, r.city || r.location, r.regionCode ? `, ${r.regionCode}` : ""), /*#__PURE__*/React.createElement("td", {
      className: "r"
    }, num(r.views != null ? r.views : r.impressions)), /*#__PURE__*/React.createElement("td", {
      className: "r"
    }, num(r.sessions))))))) : null, topPages.length ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "pc-sec-h"
    }, "Top pages"), /*#__PURE__*/React.createElement("table", {
      className: "pc-mini"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Page"), /*#__PURE__*/React.createElement("th", {
      className: "r"
    }, "Views"))), /*#__PURE__*/React.createElement("tbody", null, topPages.map((r, i) => /*#__PURE__*/React.createElement("tr", {
      key: i
    }, /*#__PURE__*/React.createElement("td", null, r.href ? /*#__PURE__*/React.createElement("a", {
      className: "pc-pagelink",
      href: r.href,
      target: "_blank",
      rel: "noreferrer"
    }, r.label) : r.label), /*#__PURE__*/React.createElement("td", {
      className: "r"
    }, num(r.views))))))) : null));
  }
  renderProducts(products) {
    const shown = (products || []).filter(Boolean).slice(0, 12);
    if (!shown.length) return null;
    return /*#__PURE__*/React.createElement("section", {
      className: "pc-card"
    }, /*#__PURE__*/React.createElement("b", {
      className: "pc-card-title"
    }, "Products viewed"), /*#__PURE__*/React.createElement("div", {
      className: "pc-pgrid"
    }, shown.map((p, i) => {
      const title = p.title && String(p.title) !== String(p.id) ? p.title : "";
      const inner = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: "pc-pgrid-img",
        style: {
          background: tintFor(title || p.id)
        }
      }, p.imageLink ? /*#__PURE__*/React.createElement("img", {
        src: p.imageLink,
        alt: "",
        loading: "lazy",
        onError: e => {
          e.target.style.display = "none";
        }
      }) : null, !p.imageLink ? /*#__PURE__*/React.createElement("span", {
        className: "pc-pgrid-ini",
        "aria-hidden": "true"
      }, (title || "?").trim().charAt(0).toUpperCase()) : null), /*#__PURE__*/React.createElement("span", {
        className: "pc-pgrid-name"
      }, title || "Untitled product"), p.id != null ? /*#__PURE__*/React.createElement("span", {
        className: "pc-muted pc-pgrid-sku"
      }, String(p.id)) : null, /*#__PURE__*/React.createElement("span", {
        className: "pc-muted pc-pgrid-sub"
      }, plural(p.views, "view", "views"), " \xB7 ", plural(p.sessions, "visit", "visits")));
      return p.link ? /*#__PURE__*/React.createElement("a", {
        key: i,
        className: "pc-pgrid-item",
        href: p.link,
        target: "_blank",
        rel: "noreferrer"
      }, inner) : /*#__PURE__*/React.createElement("span", {
        key: i,
        className: "pc-pgrid-item"
      }, inner);
    })));
  }
  permissionLines() {
    const c = this.state.consent;
    if (c === undefined) return ["Checking…"];
    if (c === null) return ["No permission on record. Ask before you email them."];
    const lines = [];
    const sup = g(c, "suppressions") || [];
    const reasons = sup.map(s => String(g(s, "reason") || "").toLowerCase());
    const optIn = g(c, "emailOptIn");
    const when = monthDay(g(c, "emailOptInDate"));
    if (optIn === true) {
      const src = sourceWords(g(c, "optInSource"));
      lines.push(`Said yes${when ? ` on ${when}` : ""}${src ? ` through ${src}` : ""}.`);
    } else if (optIn === false || reasons.some(r => ASKED_OUT.includes(r))) {
      const out = sup.find(s => ASKED_OUT.includes(String(g(s, "reason") || "").toLowerCase()));
      const d = monthDay(out ? g(out, "createdDate") || g(out, "date") : g(c, "modifiedDate"));
      const why = out ? String(g(out, "reason") || "").toLowerCase() : "unsubscribe";
      const what = why === "complaint" ? "Marked your email as spam" : why === "manual" ? "Blocked from email by your team" : why === "global_block" ? "Blocked from all email" : "Asked not to get email";
      lines.push(`${what}${d ? ` on ${d}` : ""}.`);
    } else {
      lines.push("No permission on record. Ask before you email them.");
    }
    if (reasons.some(r => HARD_BOUNCE.includes(r))) lines.push("Can't get email: address doesn't exist.");
    return lines;
  }
  renderPermission() {
    return /*#__PURE__*/React.createElement("section", {
      className: "pc-card"
    }, /*#__PURE__*/React.createElement("b", {
      className: "pc-card-title"
    }, "Email permission"), this.permissionLines().map((l, i) => /*#__PURE__*/React.createElement("span", {
      key: i,
      className: "pc-perm"
    }, l)));
  }

  // ------------------------------------------------------------- actions

  primaryEmail() {
    const c = this.state.data && this.state.data.contact || {};
    return (c.email || []).map(e => e && e.emailAddress).filter(Boolean)[0] || "";
  }
  displayName() {
    const c = this.state.data && this.state.data.contact || {};
    return c.fullName ? titleCase(c.fullName) : this.primaryEmail();
  }
  renderEraseConfirm(name) {
    const {
      erasing,
      eraseErr
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "pc-modal-back",
      onClick: e => {
        if (e.target === e.currentTarget && !erasing) this.setState({
          eraseConfirm: false,
          eraseErr: ""
        });
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "pc-modal",
      role: "dialog",
      "aria-label": "Erase contact"
    }, /*#__PURE__*/React.createElement("b", {
      className: "pc-modal-title"
    }, "Erase ", name, "?"), /*#__PURE__*/React.createElement("p", {
      className: "pc-muted"
    }, "This removes them from Plunk and from every connected email tool. We keep only a scrambled copy of the address so an import can't add them back. It's logged and can't be undone."), eraseErr ? /*#__PURE__*/React.createElement("p", {
      className: "pc-err"
    }, eraseErr) : null, /*#__PURE__*/React.createElement("div", {
      className: "pc-modal-actions"
    }, /*#__PURE__*/React.createElement(Button, {
      disabled: erasing,
      onClick: () => this.setState({
        eraseConfirm: false,
        eraseErr: ""
      })
    }, "Keep"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: erasing,
      onClick: this.erase
    }, erasing ? "Erasing…" : "Yes, erase for good"))));
  }
  render() {
    const {
      data,
      error,
      errorMessage,
      timeline,
      moreMenu
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, {
      message: errorMessage
    });
    if (!data) return /*#__PURE__*/React.createElement(Loader, null);
    const {
      contact,
      summary,
      products,
      lifecycle,
      locations,
      pages
    } = data;
    const cust = lifecycle && lifecycle.customer;
    const email = contact.email && contact.email.length && contact.email[0].emailAddress || "";
    const name = contact.fullName ? titleCase(contact.fullName) : email || "This contact";
    const stage = stageKey(g(cust, "lifecycleStage"));
    const exp = (contact.experience || [])[0] || {};
    const company = exp.company && exp.company.name || g(cust, "name") || "";
    const st = timeline && timeline.stats || {};
    const first = st.firstSeen || summary && summary.firstSeen;
    const last = st.lastSeen || summary && summary.lastSeen;
    const who = exp.title && company ? `${exp.title} at ${titleCase(company)}` : exp.title || (company ? titleCase(company) : "");
    const subline = [who, monthDay(first) ? `first seen ${monthDay(first)}` : null, lowerWhen(last) ? `last seen ${lowerWhen(last)}` : null].filter(Boolean).join(" · ");
    const nudge = timeline && timeline.nudge;
    const person = {
      email,
      name
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view pc-detail"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pc-subhead"
    }, /*#__PURE__*/React.createElement(Link, {
      to: "/Contacts",
      className: "pc-back",
      "aria-label": "Back to contacts"
    }, /*#__PURE__*/React.createElement("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M15 18l-6-6 6-6"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "pc-subhead-text"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pc-subhead-name"
    }, /*#__PURE__*/React.createElement("h1", null, name), /*#__PURE__*/React.createElement(StageChip, {
      stage: stage
    })), subline ? /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, subline) : null), /*#__PURE__*/React.createElement("span", {
      className: "pc-grow"
    }), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        editing: true
      })
    }, "Edit"), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        enriching: true
      }),
      title: "Look them up with our data partners. Nothing changes until you review it."
    }, "Fill in details"), /*#__PURE__*/React.createElement("span", {
      className: "pc-menu-wrap",
      ref: el => {
        this._moreEl = el;
      }
    }, /*#__PURE__*/React.createElement(Button, {
      "aria-label": "More actions",
      onClick: () => this.setState({
        moreMenu: !moreMenu
      })
    }, "\xB7\xB7\xB7"), moreMenu ? /*#__PURE__*/React.createElement("div", {
      className: "pc-menu"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      onClick: this.exportContact
    }, /*#__PURE__*/React.createElement("b", null, "Export"), /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, "Download their details as a spreadsheet")), isAdministrator() ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "danger",
      onClick: () => this.setState({
        moreMenu: false,
        eraseConfirm: true
      })
    }, /*#__PURE__*/React.createElement("b", null, "Erase contact"), /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, "For privacy requests. Can't be undone.")) : null) : null)), /*#__PURE__*/React.createElement("div", {
      className: "pc-cols"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pc-col"
    }, nudge ? /*#__PURE__*/React.createElement("section", {
      className: "pc-card pc-nudge"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pc-nudge-text"
    }, /*#__PURE__*/React.createElement("b", null, nudge.title || "Good time to reach out"), nudge.text ? /*#__PURE__*/React.createElement("span", {
      className: "pc-muted"
    }, nudge.text) : null), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        picker: true
      })
    }, "Add to a list"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      onClick: () => this.props.history.push(`/Email/campaigns/new?person=${encodeURIComponent(this.id())}&name=${encodeURIComponent(this.displayName())}&email=${encodeURIComponent(this.primaryEmail())}`)
    }, "Send an email")) : null, this.renderProducts(products), this.renderHistory()), /*#__PURE__*/React.createElement("div", {
      className: "pc-col pc-rail"
    }, this.renderStats(contact, summary, lifecycle), this.renderAbout(contact, lifecycle), this.renderWhereWhat(locations, pages), this.renderPermission())), this.state.picker ? /*#__PURE__*/React.createElement(ListPicker, {
      people: [person],
      onClose: () => this.setState({
        picker: false
      })
    }) : null, this.state.eraseConfirm ? this.renderEraseConfirm(name) : null, this.state.enriching ? /*#__PURE__*/React.createElement(EnrichModal, {
      personId: this.id(),
      contactName: contact.fullName || "",
      onClose: () => this.setState({
        enriching: false
      }),
      onApplied: () => {
        this.setState({
          enriching: false
        });
        this.load();
      }
    }) : null, this.state.editing ? /*#__PURE__*/React.createElement(EditContactModal, {
      personId: this.id(),
      contact: contact,
      contactName: contact.fullName || "",
      onClose: () => this.setState({
        editing: false
      }),
      onSaved: () => {
        this.setState({
          editing: false
        });
        this.load();
      }
    }) : null);
  }
}
export const ContactDetailViewComponent = withRouter(ContactPageComponent);
export default {
  ContactDetailViewComponent
};